import {Component, ComponentChild, h} from 'preact'
import EmptyPage from '../empty-page/EmptyPage'
import Switcher from '../control/Switcher'
import Panel from '../panel'
import axios from 'axios'
import AlertComponent from '../ui-components/AlertComponent'
import Header from '../header'
import globals from '../../utils/globals'
import ButtonControl from '../button-control'
import {DeleteModal, EditModal, MergeModal} from './PublicTagModals'
import Table from '../table/Table'
import PaginationComponent, {PaginationProps} from '../ui-components/PaginationComponent'
import Loader from '../ui-components/Loader'
import TagsIndex, {TagHit} from '../algolia/TagsIndex'
import * as _ from 'underscore'
import Icon from '../assets'
import Checkbox from '../ui-components/Checkbox'

interface Row {
  header?: string | ComponentChild,
  accessor: string,
  cell: string | ComponentChild,
  colspan?: number
}

interface Props {
  tagCreationSetting: boolean,
  showTagCreationSetting: boolean
}

interface State {
  tags: Array<TagHit>,
  isTagCreationEnabled: boolean,
  messageType?: string | null,
  message?: string,
  modalErrorMessage?: string,
  showCreateModal: boolean,
  isLoading: boolean,
  isPaginationSet: boolean,
  pagination: PaginationProps,
  tagToEdit?: TagHit,
  tagToDelete?: TagHit,
  searchText: string,
  retries: number,
  tagsToMerge: Array<TagHit>,
  selectAllTagsOnPage: boolean,
  showMergeModal: boolean
}

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 25

const DEFAULT_PAGINATION = {
  pagination: {
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE
  }
}
const PAGINATION_STORAGE_KEY = 'tagsPagination'
const DEFAULT_ERROR_MESSAGE = 'Something went wrong, please try again.'

class PublicTagOverview extends Component<Props, State> {
  private index: TagsIndex

  constructor(props: Props) {
    super(props)
    this.index = new TagsIndex()

    this.state = {
      tags: [],
      isTagCreationEnabled: props.tagCreationSetting,
      messageType: null,
      message: '',
      modalErrorMessage: '',
      showCreateModal: false,
      isLoading: false,
      isPaginationSet: false,
      pagination: {
        page: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE
      },
      searchText: '',
      retries: 0,
      tagsToMerge: [],
      selectAllTagsOnPage: false,
      showMergeModal: false
    }

    this.onTagCreationToggle = this.onTagCreationToggle.bind(this)
    this.onPageChange = this.onPageChange.bind(this)
    this.onPageSizeChange = this.onPageSizeChange.bind(this)
    this.onClickEditModalInput = this.onClickEditModalInput.bind(this)
    this.selectAllTags = this.selectAllTags.bind(this)
    this.selectTag = this.selectTag.bind(this)

    let element: HTMLInputElement | null = document.querySelector('input#csrftoken')

    if (element !== null) {
      this.csrf = element.value
    }
  }

  csrf: string = ''

  async componentDidMount() {

    if (!window.sessionStorage.getItem(PAGINATION_STORAGE_KEY)) {
      window.sessionStorage.setItem(PAGINATION_STORAGE_KEY, JSON.stringify(DEFAULT_PAGINATION))
    } else {
      let item = window.sessionStorage.getItem(PAGINATION_STORAGE_KEY)
      const storedPagination = item ? JSON.parse(item) : null
      const {pagination: {pageSize, page}} = storedPagination
      const isPaginationSet = JSON.stringify(storedPagination) !== JSON.stringify(DEFAULT_PAGINATION)
      const newPagination = this.state.pagination
      newPagination.pageSize = pageSize
      newPagination.page = page
      this.setState({pagination: newPagination, isPaginationSet: isPaginationSet})
    }

    await this.fetchTags(this.state.pagination.page, this.state.pagination.pageSize)
  }

  async onTagCreationToggle() {
    this.setState({messageType: null})
    const successMessage = 'Setting has been updated successfully.'
    const errorMessage = 'Something went wrong, setting can\'t be updated. Please try again.'

    try {
      await axios.post('/settings/tags/toggleTagCreation', {
        isActive: !this.state.isTagCreationEnabled
      })
      this.setState({
        isTagCreationEnabled: !this.state.isTagCreationEnabled,
        message: successMessage,
        messageType: 'success'
      })
    } catch (e) {
      this.setState({messageType: 'error', message: errorMessage})
    }
  }

  async createTag(newTag: string) {
    if (!newTag) {
      this.setState({modalErrorMessage: 'Please enter tag name', isLoading: false})
      return
    }

    try {
      const res = await axios.put(`/settings/tags/create`, {tagName: newTag})
      let tags = this.state.tags
      if (this.state.pagination.pageSize && this.state.pagination.page && this.state.pagination.total
        && this.state.pagination.page == Math.ceil(this.state.pagination.total / this.state.pagination.pageSize)
        && this.state.tags.length < this.state.pagination.pageSize) {
        tags.push({
          id: res.data.id,
          name: res.data.name,
          count: 0
        })
      }
      let pagination = this.state.pagination
      pagination.total = pagination.total ? pagination.total + 1 : pagination.total
      this.setState({
        tags: tags,
        showCreateModal: false,
        message: `Public tag '${newTag}' has been added!`,
        messageType: 'success',
        isLoading: false,
        pagination: pagination
      })
    } catch (err) {
      let errorMessage = err.response?.data?.errors || DEFAULT_ERROR_MESSAGE
      this.setState({modalErrorMessage: errorMessage, isLoading: false, messageType: null})
      return
    }
  }

  async editTag(tagToEdit: TagHit, newName: string) {
    if (!newName) {
      this.setState({modalErrorMessage: 'Please enter tag name', isLoading: false})
      return
    }

    if (tagToEdit.name === newName) {
      this.setState({
        tagToEdit: undefined,
        isLoading: false
      })
      return
    }

    try {
      const {page, pageSize} = this.state.pagination
      await axios.post(`/settings/tags/${tagToEdit.id}`,
        {
          oldName: tagToEdit.name,
          newName: newName,
          page: page,
          pageSize: pageSize
        }
      )
      const tags = this.state.tags.map((tag: TagHit) => {
        if (tag.id !== tagToEdit.id) return tag
        return {
          ...tag,
          name: newName
        }
      })
      this.setState({
        tags: tags,
        message: `Public tag '${tagToEdit.name}' has been renamed to '${newName}'!`,
        messageType: 'success',
        tagToEdit: undefined,
        isLoading: false
      })
    } catch (err) {
      let errorMessage = err.response?.data?.errors || DEFAULT_ERROR_MESSAGE
      this.setState({modalErrorMessage: errorMessage, isLoading: false, messageType: null})
    }
  }

  async deleteTag(tagToDelete: TagHit) {
    const successMessage = `Public tag '${tagToDelete.name}' has been deleted.`

    try {
      await axios.delete(`/settings/tags/${tagToDelete.id}`, {
        data: {
          csrfToken: this.csrf
        }
      })
      const {page, pageSize, total} = this.state.pagination
      let currentPage = page || DEFAULT_PAGE
      if (this.state.tags.length === 1 && currentPage !== 1) {
        // the tag to delete was the last item on current page
        this.fetchTags(currentPage - 1, pageSize)
      } else {
        let tags = this.state.tags
        tags = tags.filter((tag: TagHit) => tag.id != tagToDelete.id)
        const { hits } = await this.index.getFirstRecordsFromNextPage(currentPage, pageSize)
        if (hits.length === 1) {
          tags.push(hits[0])
        }
        this.index.clearCache()
        this.setState({
          tags: tags,
          pagination: {
            page: currentPage,
            pageSize: pageSize,
            total: total ? total - 1 : total
          }
        })
      }
      this.setState({
        isLoading: false,
        message: successMessage,
        messageType: 'success'
      })
    } catch (err) {
      this.setState({message: DEFAULT_ERROR_MESSAGE, messageType: 'error', isLoading: false})
      return
    }
  }

  async mergeTag (newName: string) {
    if (!newName) {
      this.setState({modalErrorMessage: 'Please enter tag name', isLoading: false})
      return
    }
    const {page, pageSize, total} = this.state.pagination
    const currentPage = page || DEFAULT_PAGE
    const tagIdsToMerge = this.state.tagsToMerge.map(t => t.id)
    const tags = this.state.tags
    const tagsSelectedToMerge = this.state.tagsToMerge

    try {
      const { data } = await axios.post(`/settings/tags/mergeTags`,
          {
            newName: newName,
            tagsToMerge: tagIdsToMerge
          }
      )

      let mergedTag = {
        id: data.id,
        name: data.name,
        count: 'recalculation in progress...',
        selected: false
      }
      // remove tag from the tagsSelectedToMerge list if the merged tag is named as one of the selected tags
      const mergedTags = tagsSelectedToMerge.filter(tag => tag.id !== mergedTag.id)
      let mergedTagsCount = mergedTags.length
      let differenceInTotal = mergedTagsCount
      // filter out the merged tags from updated tags
      let updatedTags = tags.filter(d => !mergedTags.includes(d))
      // check if a new tag was created due to merge tags action
      const isMergedTagNotInUpdatedTags = updatedTags.filter( tag => tag.name === mergedTag.name).length === 0
      // if a new tag was created as a result of the merge, add it to updated tags list to display it in the table
      if (isMergedTagNotInUpdatedTags) {
        updatedTags.push(mergedTag)
        //decrement merged count by 1 to account for the new tag in the total
        differenceInTotal = mergedTagsCount - 1
      }
      updatedTags.map(t => t.selected = false)

      if (total && pageSize && total > pageSize) {
        const {hits} = await this.index.getFirstRecordsFromNextPage(currentPage, pageSize, mergedTags.length);
        updatedTags.push(...hits)
      }

      this.setState({
        tags: updatedTags,
        message: `${mergedTagsCount} public tags have been merged to '${mergedTag.name}'!`,
        messageType: 'success',
        tagToEdit: undefined,
        isLoading: false,
        showMergeModal: false,
        selectAllTagsOnPage: false,
        tagsToMerge: [],
        pagination: {
          page: currentPage,
          pageSize: pageSize,
          total: total ? total - differenceInTotal : total
        }
      })
      this.index.clearCache()
    } catch (err) {
      let errorMessage = err.response?.data?.errors || DEFAULT_ERROR_MESSAGE
      this.setState({modalErrorMessage: errorMessage, isLoading: false, messageType: null})
    }
  }

  onPageChange(page: number) {
    const pageSize = this.state.pagination.pageSize
    this.updatePagination(page, pageSize)
    this.fetchTags(page, pageSize)
  }

  onPageSizeChange(pageSize: number) {
    const page = DEFAULT_PAGE
    this.updatePagination(page, pageSize)
    this.fetchTags(page, pageSize)
  }

  updatePagination(page: number, pageSize: number | undefined) {
    const newPagination = this.state.pagination
    newPagination.page = page
    newPagination.pageSize = pageSize
    this.setState({pagination: newPagination, tagsToMerge: [], selectAllTagsOnPage: false})
    this.saveSessionPagination(page, pageSize)
  }

  saveSessionPagination(page: number, pageSize: number | undefined) {
    let item = window.sessionStorage.getItem(PAGINATION_STORAGE_KEY)
    let storage = item ? JSON.parse(item) : null
    storage['pagination'] = {page, pageSize}
    window.sessionStorage.setItem(PAGINATION_STORAGE_KEY, JSON.stringify(storage))
  }

  fetchTags = _.debounce(async (page: number = DEFAULT_PAGE, pageSize: number = DEFAULT_PAGE_SIZE) => {
    this.setState({isLoading: true})
    try {
      const {hits, nbHits} = await this.index.searchByName(this.state.searchText, page, pageSize)
      if (hits.length === 0 && page > 1) {
        this.fetchTags(Math.ceil(nbHits / pageSize), pageSize)
        return
      }
      const newPagination = this.state.pagination
      newPagination.total = nbHits
      newPagination.pageSize = pageSize
      newPagination.page = page
      this.setState({tags: hits, isLoading: false, pagination: newPagination, messageType: undefined, message: undefined, retries: 0 })
    } catch (e) {
      if (this.state.retries < 3) {
        this.setState({ retries: this.state.retries + 1 })
        this.fetchTags(page, pageSize)
      } else {
        this.setState({ isLoading: false, messageType: 'error', message: 'Failed to load tags after three retries. Try again later.' })
      }
    }
  }, 500)

  onClickEditModalInput() {
    if (this.state.modalErrorMessage) {
      this.setState({modalErrorMessage: undefined})
    }
  }

  selectAllTags (value: boolean) {
    let tags = this.state.tags;
    tags.map(t => t.selected = value)
    let selectedTags = value ? tags : []
    this.setState({selectAllTagsOnPage: value, tagsToMerge: selectedTags, tags: tags})
  }

  selectTag (tag: TagHit, value: boolean) {
    let tags = this.state.tagsToMerge
    tag.selected = value
    if (!value && tags.indexOf(tag) !== -1) {
      let filteredTags = tags.filter(t => t.id != tag.id);
      this.setState({ tagsToMerge: filteredTags })
      return
    }

    tags.push(tag)
    this.setState({ tagsToMerge: tags })
  }

  tagWithHighestCount (): string {
    let selectedTags = this.state.tagsToMerge
    let tag = selectedTags.reduce((previousTag, currentTag) => (previousTag.count > currentTag.count) ? previousTag : currentTag)
    return tag.name
  }

  render({showTagCreationSetting}: Props, {isTagCreationEnabled, messageType, message, showCreateModal, isLoading, tags, pagination, tagToEdit, tagToDelete, modalErrorMessage, tagsToMerge, selectAllTagsOnPage, showMergeModal}: State) {
    const isVisibleFor = messageType === 'success' ? 5000 : false
    const defaultCdn = globals('defaultCdn')
    const paginationOptions = {
      pagination,
      onPageChange: this.onPageChange,
      onPageSizeChange: this.onPageSizeChange
    }
    const totalCountText = pagination.total === 1 ? 'Public tag' : 'Public tags'
    const showMergeButtonInHeader = tagsToMerge.length > 1 || selectAllTagsOnPage
    const mergeButton = <ButtonControl
            class='qa-merge-public-tag'
            newStyle
            variant='brand'
            title='Merge tags'
            onClick={() => this.setState({showMergeModal: true, })}
        />

    const customButtons = [
      <div class='tag-search-input-wrapper'>
        <Icon icon='search' size={18} color='#6B707E' />
        <input type='text' placeholder='search' class='form-control' onInput={(e: any) => {
          if (e.target instanceof HTMLInputElement) {
            this.setState({searchText: e.target.value}, () => this.fetchTags(this.state.pagination.page, this.state.pagination.pageSize))
          }
        }} />
      </div>,
      <ButtonControl
        class='qa-add-public-tag'
        newStyle
        variant='brand'
        title='Add public tag'
        onClick={() => this.setState({showCreateModal: true})}
      />]
    if(showMergeButtonInHeader) {
      customButtons.unshift(mergeButton)
    }
    const rows = tags.map((tag: TagHit): Array<Row> => [{
      header: <Checkbox
          label=''
          onUpdate={(value: boolean) => {this.selectAllTags(value)}}
          qaClasses='qa-select-all'
          checked={selectAllTagsOnPage}
      />,
      accessor: 'tag-selector',
      cell: <Checkbox
          label=''
          onUpdate={(value: boolean) => {this.selectTag(tag, value)}}
          qaClasses='qa-select-all'
          checked={tag.selected === undefined ? false : tag.selected}
      />
    },{
      header: 'Public tags',
      accessor: 'tag-name',
      cell: <span className={`public-tag-${tag.id} qa-public-tag-${tag.id}`}>{tag.name}</span>
    }, {
      header: 'Topics tagged',
      accessor: 'usage',
      cell: <span className={`qa-public-tag-${tag.id}-usage`}>{tag.count}</span>
    }, {
      header: 'Actions',
      accessor: 'actions',
      cell: <span>
        <ButtonControl newStyle class={`public-tag-edit-${tag.id} qa-public-tag-edit-${tag.name} qa-public-tag-edit-${tag.id}`}
                       title='Edit' onClick={() => this.setState({tagToEdit: tag})}/>
         <ButtonControl newStyle
           class={`public-tag-remove-${tag.id} qa-public-tag-remove-${tag.name} qa-public-tag-remove-${tag.id}`}
           title='Remove' variant='warning' onClick={() => this.setState({tagToDelete: tag, messageType: null})}/>
      </span>
    }
    ])

    return (
      <div className='public-tag-overview qa-public-tag-overview'>
        <Header
          title='Public Tags'
          disableCancelButton
          disableSaveButton
          customButtons={customButtons}
        />
        <div>
          {isLoading && <Loader/>}
          {
            messageType ? <AlertComponent messageType={messageType} message={message} sticky renderHTML
                                          isVisibleFor={isVisibleFor}/> : null
          }
          {showTagCreationSetting &&
          <Panel title='Setting' customClass='public-tag-panel-with-margin qa-public-tag-creation-setting' collapsible>
            <Switcher
              on={isTagCreationEnabled}
              label='Allow end users to add new public tags'
              onChange={this.onTagCreationToggle}/>
          </Panel>
          }
          {tags.length > 0
            ? <div className='control-body public-tags-overview-table qa-public-tags-overview-table'>
              <div className='public-tags__table-info'>
                <span className='public-tags__counter'>{paginationOptions.pagination.total} {totalCountText}</span>
                <PaginationComponent {...paginationOptions} />
              </div>
              <Table rows={rows} rowId={'id'} classes={['public_tag__table']}/>
            </div>
            : <EmptyPage
              img_src={`${defaultCdn}/images/tags_empty_page.png`}
              header='No public tags found'
              text={'Help users find more relevant content by adding public tags.'}
              children={<ButtonControl title='Add public tag'
                                       class='qa-add-public-tag'
                                       variant='brand' newStyle onClick={() => this.setState({showCreateModal: true})}/>}
            />
          }

          {showCreateModal ?
            <EditModal onClose={() => this.setState({showCreateModal: false, modalErrorMessage: undefined})}
                       onClick={this.onClickEditModalInput}
                       onSave={(newName: string) => this.setState({isLoading: true}, () => this.createTag(newName))}
                       error={modalErrorMessage}/> : null}
          {tagToEdit ? <EditModal tag={tagToEdit.name}
                                  onClose={() => this.setState({tagToEdit: undefined, modalErrorMessage: undefined})}
                                  onClick={this.onClickEditModalInput}
                                  onSave={(newName: string) => this.setState({isLoading: true}, () => this.editTag(tagToEdit, newName))}
                                  error={modalErrorMessage}
          /> : null}
          {tagToDelete ? <DeleteModal tag={tagToDelete.name}
                                      onClose={() => this.setState({tagToDelete: undefined})}
                                      onDelete={() => this.setState({
                                        tagToDelete: undefined,
                                        isLoading: true,
                                        messageType: null
                                      }, () => this.deleteTag(tagToDelete))}
          /> : null}
          {showMergeModal ?
              <MergeModal onClose={() => this.setState({showMergeModal: false, modalErrorMessage: undefined})}
                         tag={this.tagWithHighestCount()}
                         onClick={this.onClickEditModalInput}
                         onSave={(newName: string) => this.setState({isLoading: true}, () => this.mergeTag(newName))}
                         mergeCount={tagsToMerge.length}
                         error={modalErrorMessage}/> : null}
        </div>
      </div>
    )
  }
}

export default PublicTagOverview
